import { useCallback, useState } from 'react';
import { Button, Chip, Popover, Stack, TextField, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';

export interface DashboardV3RangeOption<T extends string = string> {
    labelKey: string;
    label?: string;
    value: T;
}

interface DashboardV3RangeSelectorProps<T extends string> {
    value: T;
    options: readonly DashboardV3RangeOption<T>[];
    customFrom: string;
    customTo: string;
    onChange: (value: T) => void;
    onCustomFromChange: (value: string) => void;
    onCustomToChange: (value: string) => void;
}

export function DashboardV3RangeSelector<T extends string>({
    value,
    options,
    customFrom,
    customTo,
    onChange,
    onCustomFromChange,
    onCustomToChange,
}: DashboardV3RangeSelectorProps<T>) {
    const t = useTranslate();
    const [customAnchorEl, setCustomAnchorEl] = useState<HTMLElement | null>(null);

    const handleCustomPopoverClose = useCallback(() => {
        setCustomAnchorEl(null);
    }, []);

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                {options.map((item) => {
                    const active = value === item.value;
                    const isCustom = item.value === 'custom';

                    return (
                        <Chip
                            key={item.value}
                            label={t(item.labelKey, { _: item.label ?? item.labelKey })}
                            size="medium"
                            clickable
                            onClick={(event) => {
                                if (isCustom) {
                                    onChange(item.value);
                                    setCustomAnchorEl(event.currentTarget);
                                    return;
                                }

                                onChange(item.value);
                                handleCustomPopoverClose();
                            }}
                            variant="outlined"
                            sx={{
                                height: 30,
                                borderRadius: 2,
                                borderColor: active ? '#BB86FC' : 'rgba(255,255,255,.12)',
                                bgcolor: active ? 'rgba(187, 134, 252, .16)' : 'transparent',
                                color: active ? '#BB86FC' : 'rgba(255,255,255,.60)',
                                '& .MuiChip-label': {
                                    px: 1.4,
                                    fontSize: 13,
                                },
                            }}
                        />
                    );
                })}
            </Stack>

            <Popover
                open={Boolean(customAnchorEl)}
                anchorEl={customAnchorEl}
                onClose={handleCustomPopoverClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        bgcolor: '#10101A',
                        border: '1px solid rgba(255,255,255,0.08)',
                        p: 2,
                        mt: 1,
                        minWidth: 320,
                    },
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <TextField
                        type="date"
                        size="small"
                        value={customFrom}
                        onChange={(e) => onCustomFromChange(e.target.value)}
                        sx={{
                            bgcolor: '#141422',
                            borderRadius: 1,
                            width: 150,
                            '& .MuiInputBase-input': { color: '#FFFFFF' },
                        }}
                    />

                    <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>—</Typography>

                    <TextField
                        type="date"
                        size="small"
                        value={customTo}
                        onChange={(e) => onCustomToChange(e.target.value)}
                        sx={{
                            bgcolor: '#141422',
                            borderRadius: 1,
                            width: 150,
                            '& .MuiInputBase-input': { color: '#FFFFFF' },
                        }}
                    />

                    <Button
                        variant="contained"
                        size="small"
                        sx={{
                            textTransform: 'none',
                            bgcolor: 'rgba(187, 134, 252, .16)',
                            border: '1px solid #BB86FC',
                            color: '#BB86FC',
                            '&:hover': {
                                bgcolor: 'rgba(187, 134, 252, .24)',
                            },
                        }}
                        onClick={handleCustomPopoverClose}
                    >
                        {t('module.dashboardV3.actions.apply', { _: 'Apply' })}
                    </Button>
                </Stack>
            </Popover>
        </>
    );
}